import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface CommunityFeedGuidelinesProps {
  open: boolean;
  onAgree: () => void;
  onDecline: () => void;
}

export default function CommunityFeedGuidelines({
  open,
  onAgree,
  onDecline,
}: CommunityFeedGuidelinesProps) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-[92vw] transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all sm:p-8 md:max-w-[50vw]">
                <div className="absolute top-4 right-4">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onDecline}
                    aria-label="Close guidelines"
                    tabIndex={0}
                  >
                    <FontAwesomeIcon icon="times" className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <Dialog.Title
                      as="h2"
                      className="text-xl font-bold text-gray-900"
                    >
                      Community Feed Guidelines
                    </Dialog.Title>
                    <p className="mt-3 text-sm text-gray-600">
                      The community feed is a place for all members to build and
                      strengthen community relationships. In order to maintain a
                      positive environment, all members are requested to adhere
                      to the guidelines below.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      Be respectful of all folks.
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      While disagreements are normal, please communicate with
                      respect and kindness. Refrain from profanity and name
                      calling. Discrimination, harassment, or bullying of any
                      kind will not be tolerated.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      Limit self-promotion or ads.
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Community members are encouraged to ask for and share
                      recommendations, especially about favorite neighborhood
                      spots or services. However, please limit use of the feed
                      for self or personal promotion.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      Do not invade anyone&rsquo;s privacy.
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Do not share personal phone numbers, email addresses, or
                      any personal information of other people to the feed
                      without permission.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-gray-900">
                      Keep posts kind and helpful.
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Post content that would be relevant or interesting to the
                      community. Before posting a question directed towards
                      staff, consider if it&rsquo;d be better suited for an
                      email or phone call.
                    </p>
                  </div>

                  <p className="text-sm text-gray-600">
                    And do we have to say it? Don&rsquo;t use the feed for
                    anything illegal.
                  </p>

                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Thanks, and happy posting!
                    </h2>
                  </div>

                  <div className="flex flex-col gap-3 pt-2 md:flex-row md:justify-end md:pt-4">
                    <button
                      type="button"
                      className="w-full rounded-full bg-[#F9877E] px-6 py-3 text-sm font-medium text-white hover:bg-[#ff7a70] transition-colors md:w-auto md:min-w-[140px]"
                      onClick={onAgree}
                      tabIndex={0}
                      aria-label="Agree to community feed guidelines"
                    >
                      I Agree
                    </button>
                    <button
                      type="button"
                      className="w-full rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors md:w-auto md:min-w-[140px]"
                      onClick={onDecline}
                      tabIndex={0}
                      aria-label="Decline community feed guidelines"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
